/**
 * PerformanceTimer - Utility for measuring execution time
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for measuring execution time using
 * performance.now() and Date.now(), which is useful for performance monitoring
 * and telemetry.
 */

/**
 * Create a performance timer using performance.now()
 * 
 * Returns an object with start() and elapsed() methods for high-resolution timing.
 * Uses performance.now() which provides microsecond precision.
 * 
 * @returns Timer object with start() and elapsed() methods
 */
export function createPerformanceTimer() {
  let startTime: number | null = null;
  
  return {
    start(): void {
      startTime = performance.now();
    },
    
    elapsed(): number {
      if (startTime === null) {
        throw new Error('Timer not started. Call start() first.');
      }
      return performance.now() - startTime;
    },
    
    elapsedMs(): string {
      return this.elapsed().toFixed(2);
    }
  };
}

/**
 * Create a simple timer using Date.now()
 * 
 * Returns an object with start() and elapsed() methods for timing.
 * Uses Date.now() which provides millisecond precision.
 * 
 * @returns Timer object with start() and elapsed() methods
 */
export function createDateTimer() {
  let startTime: number | null = null;
  
  return {
    start(): void {
      startTime = Date.now();
    },
    
    elapsed(): number {
      if (startTime === null) {
        throw new Error('Timer not started. Call start() first.');
      }
      return Date.now() - startTime;
    },
    
    elapsedMs(): string {
      return this.elapsed().toString();
    }
  };
}

/**
 * Calculate duration between two timestamps
 * 
 * @param startTime - Start timestamp (from performance.now() or Date.now())
 * @param endTime - End timestamp (from performance.now() or Date.now())
 * @returns Duration in milliseconds
 */
export function calculateDuration(startTime: number, endTime: number): number {
  return endTime - startTime;
}

/**
 * Format duration as a string with 2 decimal places
 * 
 * @param duration - Duration in milliseconds
 * @returns Formatted duration string
 */
export function formatDuration(duration: number): string {
  return duration.toFixed(2);
}

